/**
 * @fileoverview View User dialog.
 * @description User Management's read-only profile, from GET /users/{userId}, in the spec's
 * sections: Contact Details, Organisation Details, Account Status and Assigned Banks (see
 * ViewUserProfile). The actions on offer (Edit, Deactivate, Reactivate, Resend invitation) come
 * from the user's `allowedActions`, which the backend works out for the caller, and each hands over
 * to the page (see ViewUserActions). Holds no resettable state, so it can stay mounted with an
 * empty userId. The data is in useViewUserDialog.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import ViewUserActions from '@/features/users/components/ViewUserActions'
import ViewUserProfile from '@/features/users/components/ViewUserProfile'
import { useViewUserDialog } from '@/features/users/hooks/useViewUserDialog'
import Modal from '@/shared/components/molecules/Modal'

import type { UserDetail } from '@/features/users/types/users.types'

interface ViewUserModalProps {
  /** The user to show, or '' to keep the dialog closed. */
  userId: string
  onClose: () => void
  onEdit: () => void
  onDeactivate: () => void
  onReactivate: () => void
  /** Resends this user's pending invitation. */
  onResendInvitation: (user: UserDetail) => void
}

/**
 * @component
 * @param {ViewUserModalProps} props - The user to show, and the dialog's callbacks.
 * @param {string} props.userId - The user to show, or '' to keep the dialog closed.
 * @param {() => void} props.onClose - Dismisses the dialog.
 * @param {() => void} props.onEdit - Opens the Edit User dialog for this user.
 * @param {() => void} props.onDeactivate - Opens the Deactivate User dialog for this user.
 * @param {() => void} props.onReactivate - Opens the Reactivate User dialog for this user.
 * @param {(user: UserDetail) => void} props.onResendInvitation - Resends the user's pending
 * invitation.
 */
function ViewUserModal({
  userId,
  onClose,
  onEdit,
  onDeactivate,
  onReactivate,
  onResendInvitation,
}: ViewUserModalProps) {
  const { t } = useTranslation()
  const { user, isLoading, error } = useViewUserDialog(userId)

  return (
    <Modal
      data-testid="view-user-modal"
      open={userId !== ''}
      onClose={onClose}
      title={t('sales.userManagement.viewUser.title')}
      subtitle={user?.displayName ?? ''}
    >
      {isLoading && (
        <p className="text-[13px] text-slate" data-testid="view-user-loading">
          {t('sales.userManagement.viewUser.loading')}
        </p>
      )}

      {error && (
        <p role="alert" className="text-[13px] text-danger" data-testid="view-user-error-message">
          {error.message || t('sales.userManagement.viewUser.loadError')}
        </p>
      )}

      {user && (
        <div className="flex flex-col gap-4">
          <ViewUserActions
            user={user}
            onEdit={onEdit}
            onDeactivate={onDeactivate}
            onReactivate={onReactivate}
            onResendInvitation={onResendInvitation}
          />
          <ViewUserProfile user={user} />
        </div>
      )}
    </Modal>
  )
}

export default ViewUserModal
